import { getAPI } from '../config'
import { ICategory, ICommandResult, IScenario } from '../../../application/src/types'

import { getAuthorizationConfig } from '../../src/config'
import { CONTEXT } from '../../../application/src/domain'

let apitoken: any = null

export const getApiToken = () => apitoken

export const setApiToken = (v: any) => (apitoken = v)

const getAuthHeader = async () => {
  let token = null
  if (apitoken) {
    const config = getAuthorizationConfig()
    token = await apitoken(config.apiscope)
  }
  return {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      context: window.localStorage.getItem(CONTEXT) || '',
    },
  }
}

const parseJsonSafely = async (res: Response) => {
  const raw = await res.text()
  if (!raw) return null

  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const handleResponse = async (res: Response) => {
  if (res.status === 401) {
    window.location.href = `/`
    return null
  } else if (res.status === 404 || res.status >= 500) {
    return null
  } else {
    return res !== null ? parseJsonSafely(res) : []
  }
}

const handleResponseException = async (res: Response) => {
  if (res.status === 401) {
    window.location.href = `/`
    return null
  } else if (res.status === 404 || res.status >= 500) {
    const r: any = await parseJsonSafely(res)
    throw new Error(r?.msg || `Request failed with status ${res.status}`)
  } else {
    return res !== null ? parseJsonSafely(res) : []
  }
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const isTransientNetworkError = (error: unknown) => {
  if (!(error instanceof Error)) return false
  return /fetch|network|timeout|load failed/i.test(error.message)
}

const service = {
  get: async (url: string, options: object, retries = 1): Promise<any> => {
    const authheader = await getAuthHeader()
    const payload = { ...options, ...authheader /*credentials: 'include'*/ }

    try {
      return await fetch(getAPI() + url, payload).then(handleResponse)
    } catch (error) {
      if (retries > 0 && isTransientNetworkError(error)) {
        await wait(1500)
        return service.get(url, options, retries - 1)
      }
      console.warn(`GET request failed for ${url}`, error)
      return []
    }
  },
  post: async (url: string, body: object, options: object = {}) => {
    const authheader = await getAuthHeader()
    try {
      return await fetch(getAPI() + url, {
        ...options,
        ...authheader,
        /*credentials: 'include',*/ method: 'POST',
        body: JSON.stringify(body),
      }).then(handleResponseException)
    } catch (error) {
      console.warn(`POST request failed for ${url}`, error)
      throw error
    }
  },
  put: async (url: string, body: object, options: object = {}) => {
    const authheader = await getAuthHeader()
    try {
      return await fetch(getAPI() + url, {
        ...options,
        ...authheader,
        /*credentials: 'include',*/ method: 'PUT',
        body: JSON.stringify(body),
      }).then(handleResponse)
    } catch (error) {
      console.warn(`PUT request failed for ${url}`, error)
      return null
    }
  },
  delete: async (url: string, options: object = {}) => {
    const authheader = await getAuthHeader()
    try {
      return await fetch(getAPI() + url, {
        ...options,
        ...authheader,
        /*credentials: 'include',*/ method: 'DELETE',
      }).then(handleResponse)
    } catch (error) {
      console.warn(`DELETE request failed for ${url}`, error)
      return null
    }
  },
}

export async function queryScenarios(
  scenariocontext: string,
  page: number,
  pageSize: number,
  term: string,
  status: string[],
  tag: string[],
): Promise<any> {
  return service.get(
    `/ModelService/scenarios?page=${page}&pageSize=${pageSize}&scenariocontext=${encodeURIComponent(scenariocontext)}&term=${term}&status=${status}&tag=${tag}`,
    {},
  )
}

export async function queryScenarioContexts(scenariocontext: string): Promise<any[]> {
  return service.get(`/ModelService/scenariocontexts?scenariocontext=${encodeURIComponent(scenariocontext)}`, {})
}

export async function queryAllScenarios(): Promise<IScenario[]> {
  return service.get(`/ModelService/scenarios`, {})
}

export async function getRemotes(): Promise<any> {
  return service.get(`/remotes`, {})
}

export async function getApplicationConfig(): Promise<any> {
  return service.get(`/ConfigService`, {})
}

export async function getScenario(scenarioId: string): Promise<IScenario | undefined> {
  if (scenarioId === 'create') return undefined
  const result = await service.get(`/ModelService/scenarios/${scenarioId}`, {})
  return result
}

export async function queryNameAvailable(context: string, name: string): Promise<boolean> {
  return service.get(`/ModelService/nameavailable/${context}/${name}`, {})
}

export async function createModelScenario(data: any): Promise<ICommandResult> {
  const result = await service.post('/ModelService/scenarios', data, {})
  return result
}

export async function deleteModelScenario(id: string): Promise<void> {
  return service.delete(`/ModelService/scenarios/${id}`, {})
}

export async function createCategory(data: any): Promise<ICategory> {
  return service.post('/ModelService/categories', data, {})
}

export async function deleteCategory(categoryId: string): Promise<any> {
  return service.delete(`/ModelService/categories/${categoryId}`, {})
}

export async function queryCategories(): Promise<ICategory[]> {
  return service.get(`/ModelService/categories`, {})
}
